import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import {
  getDataFromLocalStorage,
  getPosInactivityTimeoutSeconds,
  isSharedPosStaffLoggedIn,
  logOffPosStaff,
  POS_IDLE_WARNING_SECONDS,
} from "./util";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "wheel"];
/** Same window POS uses when restoring the cart. */
const POS_CART_CACHE_MS = 5 * 60 * 1000;

function hasInProgressCart() {
  const cart = getDataFromLocalStorage("cart", POS_CART_CACHE_MS);
  return Array.isArray(cart?.items) && cart.items.length > 0;
}

export default function usePosIdleLogoff() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(null);
  const warningTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const idleSeconds = getPosInactivityTimeoutSeconds(user);
  const enabled =
    idleSeconds > 0 && isSharedPosStaffLoggedIn() && location.pathname !== "/access-code";

  useEffect(() => {
    if (!enabled) {
      setCountdown((prev) => (prev == null ? prev : null));
      return;
    }

    const idleMs = idleSeconds * 1000;
    const warningMs = Math.min(POS_IDLE_WARNING_SECONDS, idleSeconds) * 1000;

    const clearTimers = () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (tickTimerRef.current) {
        clearInterval(tickTimerRef.current);
        tickTimerRef.current = null;
      }
    };

    const hideCountdown = () => {
      setCountdown((prev) => (prev == null ? prev : null));
    };

    function resetTimer() {
      lastActivityRef.current = Date.now();
      clearTimers();
      hideCountdown();
      warningTimerRef.current = setTimeout(startWarningTick, Math.max(0, idleMs - warningMs));
    }

    function startWarningTick() {
      const tick = () => {
        if (hasInProgressCart()) {
          resetTimer();
          return;
        }
        const remaining = Math.ceil((idleMs - (Date.now() - lastActivityRef.current)) / 1000);
        if (remaining <= 0) {
          clearTimers();
          hideCountdown();
          logOffPosStaff(navigate);
          return;
        }
        setCountdown(remaining);
      };
      tick();
      tickTimerRef.current = setInterval(tick, 1000);
    }

    resetTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer, { capture: true, passive: true });
    });

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimer, { capture: true });
      });
    };
  }, [enabled, idleSeconds, navigate]);

  return countdown;
}
